import React from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, X } from "lucide-react";

interface MatchBreakdownModalProps {
  open: boolean;
  candidateName: string;
  opportunityTitle?: string;
  resumeUrl?: string | null;
  matchBreakdown: any;
  matchScore?: number | null;
  candidateId?: string | null;
  onClose: () => void;
}

const getScoreTheme = (score: number) => {
  if (score >= 75) return { label: "Strong Match", accent: "#059669", bg: "#ecfdf5", ring: "#10b981" };
  if (score >= 50) return { label: "Good Match", accent: "#b45309", bg: "#fffbeb", ring: "#f59e0b" };
  return { label: "Weak Match", accent: "#64748b", bg: "#f1f5f9", ring: "#94a3b8" };
};

const getMetricTheme = (score: number) => {
  if (score >= 75) return { color: "#059669", bar: "#10b981", bg: "rgba(5,150,105,0.08)" };
  if (score >= 50) return { color: "#b45309", bar: "#f59e0b", bg: "rgba(245,158,11,0.08)" };
  return { color: "#dc2626", bar: "#f16565", bg: "rgba(239,68,68,0.06)" };
};

const initials = (name: string) => name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase() || "?";

const getAvatarColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const palette = ["#059669", "#0891b2", "#7c3aed", "#db2777", "#d97706", "#0284c7"];
  return palette[Math.abs(hash) % palette.length];
};

interface MetricCardProps { label: string; score: number; }
const MetricCard: React.FC<MetricCardProps> = ({ label, score }) => {
  const theme = getMetricTheme(score);
  return (
    <div style={{
      background: theme.bg, border: `1px solid ${theme.color}20`,
      borderRadius: "10px", padding: "12px 14px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
        <span style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>{label}</span>
        <span style={{ fontSize: "13px", fontWeight: 800, color: theme.color }}>{score}</span>
      </div>
      <div style={{ height: "5px", background: "rgba(0,0,0,0.08)", borderRadius: "999px", overflow: "hidden" }}>
        <div style={{ width: `${score}%`, height: "100%", background: theme.bar, borderRadius: "999px" }} />
      </div>
    </div>
  );
};

const MatchBreakdownModal: React.FC<MatchBreakdownModalProps> = ({
  open,
  candidateName,
  opportunityTitle = "Job Opportunity",
  resumeUrl,
  matchBreakdown,
  matchScore = 0,
  candidateId,
  onClose,
}) => {
  const navigate = useNavigate();
  if (!open || !matchBreakdown) return null;

  const score = matchScore || 0;
  const theme = getScoreTheme(score);
  const avatarBg = getAvatarColor(candidateName);

  // SVG ring
  const radius = 32;
  const circ = 2 * Math.PI * radius; // ≈201
  const offset = circ - (circ * score) / 100;

  const metrics = [
    { label: "Skills", score: matchBreakdown.skills_score || 0, reason: matchBreakdown.skills_reason },
    { label: "Proficiency", score: matchBreakdown.proficiency_score || 0, reason: matchBreakdown.proficiency_reason },
    { label: "Experience", score: matchBreakdown.experience_score || 0, reason: matchBreakdown.experience_reason },
    { label: "Education", score: matchBreakdown.education_score || 0, reason: matchBreakdown.education_reason },
  ].filter(m => m.score != null);

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(15,23,42,0.45)",
        backdropFilter: "blur(4px)",
        padding: "20px",
        animation: "fadeIn 0.2s ease",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%", maxWidth: "480px", maxHeight: "90vh",
          background: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(20px)",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18), 0 8px 24px rgba(5,150,105,0.12)",
          border: "1px solid rgba(5,150,105,0.15)",
          display: "flex", flexDirection: "column",
          animation: "scaleIn 0.25s cubic-bezier(0.16,1,0.3,1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div style={{
          background: "linear-gradient(135deg, #064e3b 0%, #065f46 60%, #047857 100%)",
          padding: "24px 24px 20px",
          position: "relative",
          flexShrink: 0,
        }}>
          {/* Close */}
          <button
            onClick={onClose}
            style={{
              position: "absolute", top: "16px", right: "16px",
              width: "28px", height: "28px", borderRadius: "50%",
              background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
              color: "rgba(255,255,255,0.7)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.2)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
          >
            <X size={14} />
          </button>

          {/* Candidate info */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "18px" }}>
            <div style={{
              width: "40px", height: "40px", borderRadius: "50%",
              background: avatarBg, display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 800, fontSize: "15px",
              boxShadow: `0 0 0 3px rgba(255,255,255,0.2)`,
            }}>
              {initials(candidateName)}
            </div>
            <div>
              <div style={{ fontSize: "15px", fontWeight: 700, color: "#ffffff" }}>{candidateName}</div>
              <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.55)", marginTop: "2px" }}>{opportunityTitle}</div>
            </div>
          </div>

          {/* Score ring + label */}
          <div style={{ display: "flex", alignItems: "center", gap: "18px" }}>
            {/* Ring */}
            <div style={{ position: "relative", width: "74px", height: "74px", flexShrink: 0 }}>
              <svg width="74" height="74" viewBox="0 0 74 74" style={{ transform: "rotate(-90deg)" }}>
                <circle cx="37" cy="37" r={radius} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="7" />
                <circle
                  cx="37" cy="37" r={radius} fill="none"
                  stroke={theme.ring} strokeWidth="7"
                  strokeDasharray={circ}
                  strokeDashoffset={offset}
                  strokeLinecap="round"
                  style={{ transition: "stroke-dashoffset 0.6s cubic-bezier(0.4,0,0.2,1)" }}
                />
              </svg>
              <div style={{
                position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "20px", fontWeight: 900, color: "#fff",
              }}>
                {Math.round(score)}
              </div>
            </div>

            <div>
              <div style={{ fontSize: "19px", fontWeight: 800, color: theme.ring }}>{theme.label}</div>
              <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.5)", marginTop: "3px" }}>out of 100 points</div>
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 22px 22px" }}>
          {/* Metric Grid */}
          {metrics.length > 0 && (
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: "10px", marginBottom: "20px",
            }}>
              {metrics.map(m => <MetricCard key={m.label} label={m.label} score={m.score} />)}
            </div>
          )}

          {/* Reasons */}
          {metrics.filter(m => m.reason).map((m) => {
            const t = getMetricTheme(m.score);
            return (
              <div key={m.label} style={{ marginBottom: "14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: "4px" }}>
                  <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: t.bar, flexShrink: 0 }} />
                  <span style={{ fontSize: "12.5px", fontWeight: 700, color: "#1e293b" }}>{m.label}</span>
                </div>
                <p style={{ fontSize: "13px", color: "#64748b", lineHeight: 1.55, margin: 0, paddingLeft: "15px" }}>
                  {m.reason}
                </p>
              </div>
            );
          })}

          {/* Divider */}
          <div style={{ height: "1px", background: "#f1f5f9", margin: "16px 0" }} />

          {/* Matched Skills */}
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>
              Matched Skills
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
              {(!matchBreakdown.matched_skills || matchBreakdown.matched_skills.length === 0)
                ? <span style={{ fontSize: "12.5px", color: "#94a3b8", fontStyle: "italic" }}>No matched skills</span>
                : matchBreakdown.matched_skills.map((s: string) => (
                  <span key={s} style={{
                    fontSize: "12px", fontWeight: 600, padding: "5px 11px", borderRadius: "999px",
                    background: "rgba(5,150,105,0.08)", color: "#059669",
                    border: "1px solid rgba(5,150,105,0.2)",
                  }}>{s}</span>
                ))
              }
            </div>
          </div>

          {/* Divider */}
          <div style={{ height: "1px", background: "#f1f5f9", margin: "16px 0" }} />

          {/* Missing Skills */}
          <div style={{ marginBottom: "4px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>
              Missing Skills
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
              {(!matchBreakdown.missing_skills || matchBreakdown.missing_skills.length === 0)
                ? <span style={{ fontSize: "12.5px", color: "#94a3b8", fontStyle: "italic" }}>No missing skills</span>
                : matchBreakdown.missing_skills.map((s: string) => (
                  <span key={s} style={{
                    fontSize: "12px", fontWeight: 600, padding: "5px 11px", borderRadius: "999px",
                    background: "rgba(239,68,68,0.07)", color: "#dc2626",
                    border: "1px solid rgba(239,68,68,0.2)",
                  }}>{s}</span>
                ))
              }
            </div>
          </div>

          {/* Portfolio Button */}
          {candidateId && (
            <button
              onClick={() => {
                navigate(`/dashboard/portfolio/detail?candidate_id=${encodeURIComponent(candidateId || "")}`);
                onClose();
              }}
              style={{
                width: "100%", marginTop: "20px", padding: "11px",
                background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                color: "#fff", border: "none", borderRadius: "10px",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                fontWeight: 700, fontSize: "0.88rem", cursor: "pointer",
                boxShadow: "0 4px 14px rgba(5,150,105,0.3)",
                transition: "opacity 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              <ExternalLink size={15} /> View Full Portfolio
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.94) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes kan-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
        .kan-pulse-dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: #94a3b8; animation: kan-pulse 1.4s infinite; }
      `}</style>
    </div>
  );
};

export default MatchBreakdownModal;
